import { useContext, useEffect, useState } from 'react'
import { APIContext } from 'context/APIContext'
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid'

import ProgramLevelDropdown from 'components/Program/ProgramLevelDropdown'
import MacroVolumeFocusTypeDropdown from 'components/Program/MacroVolumeConfig/MacroVolumeFocusTypeDropdown'
import MesoVolume from 'components/MesoVolume/MesoVolume'

const IsSelectedMesoVolumeLeveTypeAll = (selectedMesoVolumeLevelType) => {
    return selectedMesoVolumeLevelType === 'all' || selectedMesoVolumeLevelType === '' || selectedMesoVolumeLevelType == null
}

export default function MesoVolumeConfigLibrary({ programMetadata = null }) {

    const { GetMesoVolumeConfigs } = useContext(APIContext)

    const [mesoVolumeConfigs, setMesoVolumeConfigs] = useState(null)
    const [expanded, setExpanded] = useState(false)
    const [macroVolumeFocusTypeFilter, setMacroVolumeFocusTypeFilter] = useState('all')
    const [selectedMesoVolumeLevelType, setSelectedMesoVolumeLevelType] = useState('all')
    const [selectedMesoVolumeLevel, setSelectedMesoVolumeLevel] = useState(0)

    const InitializeMesoVolumeConfigs = async () => {
        setMesoVolumeConfigs(null)
        const mesoVolumeConfigs = await GetMesoVolumeConfigs(programMetadata?.programId)
        setMesoVolumeConfigs(mesoVolumeConfigs)
    }

    const filter_1_macroVolumeFocusTypeFilteredMesoVolumeConfigs = (
        mesoVolumeConfigs === null ? null
            : macroVolumeFocusTypeFilter === 'all' ? mesoVolumeConfigs
                : mesoVolumeConfigs.filter(
                    (mesoVolumeConfig) => parseInt(mesoVolumeConfig.macroVolumeFocusTypeId) === parseInt(macroVolumeFocusTypeFilter)
                )
    )

    const filter_2_mesoVolumeLevelTypeFilteredMesoVolumeConfigs = (
        filter_1_macroVolumeFocusTypeFilteredMesoVolumeConfigs === null ? null
            : IsSelectedMesoVolumeLeveTypeAll(selectedMesoVolumeLevelType) ? filter_1_macroVolumeFocusTypeFilteredMesoVolumeConfigs
                : filter_1_macroVolumeFocusTypeFilteredMesoVolumeConfigs.filter(
                    (mesoVolumeConfig) => parseInt(mesoVolumeConfig.mesoVolumeLevelTypeId) === parseInt(selectedMesoVolumeLevelType)
                )
    )

    const filter_3_mesoVolumeLevelFilteredMesoVolumeConfigs = (
        filter_2_mesoVolumeLevelTypeFilteredMesoVolumeConfigs === null ? null
            : (!selectedMesoVolumeLevel || Number.isNaN(selectedMesoVolumeLevel)) ? filter_2_mesoVolumeLevelTypeFilteredMesoVolumeConfigs
                : filter_2_mesoVolumeLevelTypeFilteredMesoVolumeConfigs.filter(
                    (mesoVolumeConfig) => parseInt(mesoVolumeConfig.level) === parseInt(selectedMesoVolumeLevel)
                )
    )

    useEffect(() => { InitializeMesoVolumeConfigs() }, [programMetadata])

    if (programMetadata === null) return null

    return (
        <div className='w-full divide-y divide-gray-200'>
            <div 
                className='flex items-center gap-x-4 px-4 py-3.5 cursor-pointer'
                onClick={() => setExpanded(!expanded)}
            >
                <p className='flex-1 text-lg'>Program Blocks</p>

                {expanded && <ChevronUpIcon className='w-6 h-6 text-gray-600' />}
                {!expanded && <ChevronDownIcon className='w-6 h-6 text-gray-600' />}
            </div>

            {expanded &&
                <div className='w-full p-4'>
                    <div className='flex gap-x-4 pb-4'>
                        <div className='flex items-center gap-x-2'>
                            <div className='w-60'>
                                <ProgramLevelDropdown
                                    allowAll={true}
                                    programLevelHashMap={programMetadata?.programLevelHashMap}
                                    // selectedMesoVolumeLevelType={selectedMesoVolumeLevelType}
                                    // setSelectedMesoVolumeLevelType={setSelectedMesoVolumeLevelType}
                                />
                            </div>

                            <p>-</p>

                            <input
                                type="number"
                                className="w-16 p-2 border border-gray-300 rounded-md text-center"
                                min={0}
                                placeholder="All"
                                value={selectedMesoVolumeLevel || ''}
                                onChange={(event) => {
                                    const rawValue = event.target.value
                                    
                                    if (rawValue === '') {
                                        setSelectedMesoVolumeLevel(0)
                                        return
                                    }

                                    const parsedValue = parseInt(rawValue, 10)
                                    setSelectedMesoVolumeLevel(Number.isNaN(parsedValue) ? 0 : parsedValue)
                                }}
                            />
                        </div>

                        <div className='w-60'>
                            <MacroVolumeFocusTypeDropdown 
                                allowAll={true}
                                macroVolumeFocusTypeHashMap={programMetadata?.macroVolumeFocusTypeHashMap}
                                selectedMacroVolumeFocusTypeId={macroVolumeFocusTypeFilter}
                                setSelectedMacroVolumeFocusTypeId={setMacroVolumeFocusTypeFilter}
                            />
                        </div>
                    </div>

                    {mesoVolumeConfigs === null && 
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='w-full h-[260px] bg-gray-200 rounded-lg animate-pulse' />
                            <div className='w-full h-[260px] bg-gray-200 rounded-lg animate-pulse' />
                            <div className='w-full h-[260px] bg-gray-200 rounded-lg animate-pulse' />
                            <div className='w-full h-[260px] bg-gray-200 rounded-lg animate-pulse' />
                            <div className='w-full h-[260px] bg-gray-200 rounded-lg animate-pulse' />
                            <div className='w-full h-[260px] bg-gray-200 rounded-lg animate-pulse' />
                            <div className='w-full h-[260px] bg-gray-200 rounded-lg animate-pulse' />
                            <div className='w-full h-[260px] bg-gray-200 rounded-lg animate-pulse' />
                        </div>
                    }

                    {mesoVolumeConfigs !== null &&
                        <>
                            {filter_3_mesoVolumeLevelFilteredMesoVolumeConfigs?.length === 0 &&
                                <div className='py-32 text-center'>
                                    <p className='text-sm text-gray-400'>No blocks found</p>
                                </div>
                            }

                            {filter_3_mesoVolumeLevelFilteredMesoVolumeConfigs?.length > 0 &&
                                <div className='grid grid-cols-2 gap-4'>
                                    {filter_3_mesoVolumeLevelFilteredMesoVolumeConfigs.map((mesoVolume, index) => (
                                        <div 
                                            key={index} 
                                            className='min-w-0'
                                        >
                                            <MesoVolume 
                                                programMetadata={programMetadata}
                                                mesoVolume={mesoVolume} 
                                            />
                                        </div>
                                    ))}
                                </div>
                            }
                        </>
                    }
                </div>
            }
        </div>
    )
}